import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { withLocalize } from 'react-localize-redux';
import { compose } from 'recompose';
import moment from 'moment';
import { get, map, times, toLower, debounce } from 'lodash';
import { graphql } from 'react-apollo';
import axios from 'axios';
import { withFormik, Field } from 'formik';
import { Form, Icon, Header, Button } from 'semantic-ui-react';
import { Element, scroller } from 'react-scroll';
import { Link } from 'react-router-dom';

import { HHAlert, multiRoundDeadlineValidateCreate } from '../common/util';
import { ALERT_TYPE, BANNER_TEMPLATE } from '../common/constant';
import { FORM_NAME } from './formConstant';
// import { alphaNumericRegex } from './regex';
import Image from '../common/image';
import { CMS_EVENT_GET_LOCATIONS, PAGE_BUILDER_CREATE } from '../network/api';
import { EVENT_TYPE } from '../cms/constant';
import { CHECK_EVENT_SLUG } from '../events/graphql';
import { getTimeZoneOptions } from '../common/datetime';
import { EVENT_MANAGER, EVENT_CREATOR } from '../permission/constants/permissions';
import { withPermissionValidation } from '../permission';
import EventFormBasicInfo from './eventFormBasicInfo';
import EventFormImage from './eventFormImage';
import EventFormDetailInfo from './eventFormDetailInfo';
import EventFormPreview from './eventFormPreview';
import EventFormDisplay from './eventFormDisplay';
import {
  HHInput,
  HHFileUploader,
  HHLocationSelect,
  HHDatePicker,
  HHRadioGroup,
  HHCheckbox,
  HHSingleSelect,
  HHMultipleSelect
} from './fields';

/* eslint-disable func-names, prefer-arrow-callback */
Yup.addMethod(Yup.mixed, 'moment', function (message) {
  return this.test('moment', message, function (value) {
    return moment(value).isValid();
  });
});

const formikEnhancer = withFormik({
  validationSchema: ({ translate }) =>
    Yup.object().shape({
      name: Yup.string().min(3, translate('form.error.string.min'))
        .max(50, translate('form.error.string.max'))
        .required(translate('form.error.mixed.required')),
      slug: Yup.string().min(3, translate('form.error.string.min'))
        .max(50, translate('form.error.string.max'))
        .required(translate('form.error.mixed.required'))
        .matches(/^[a-zA-Z0-9]+$/, translate('form.error.validate.alphabetAndIntOnly')),
      // .matches(alphaNumericRegex, {
      //   excludeEmptyString: true,
      //   message: translate('form.error.regex.alphaNumeric')
      // }),
      externalSignupUrl: Yup.string(),
      maxTeamMembers: Yup.number().required(
        translate('form.error.mixed.required')
      ),
      bannerBg: Yup.string(),
      coverImageUrl: Yup.string(),
      timezone: Yup.string().required(translate('form.error.mixed.required')),
      start: Yup.mixed()
        .moment()
        .required(translate('form.error.mixed.required')),
      end: Yup.mixed()
        .moment()
        .test(
          'afterStartDate',
          translate('form.error.moment.afterStartDate'),
          function (value) {
            const { start } = this.parent;
            return value.isAfter(start);
          }
        )
        .required(),
      subDeadline: Yup.mixed()
        .moment()
        .test(
          'afterStartDateBeforeEndDate',
          translate('form.error.moment.betweenStartAndEndDate'),
          function (value) {
            const { start, end } = this.parent;
            return value.isBetween(start, end);
          }
        )
        .required(),
      isPrivate: Yup.boolean().required(translate('form.error.mixed.required')),
      isNotWillingToPublic: Yup.boolean(),
      isScreening: Yup.boolean().required(
        translate('form.error.mixed.required')
      ),
      isHomepageUrl: Yup.boolean().required(
        translate('form.error.mixed.required')
      ),
      // isOnline: Yup.boolean(),
      isOnline: Yup.string(),
      containStreaming: Yup.boolean().required(
        translate('form.error.mixed.required')
      ),
      contentJson: Yup.string(),
      type: Yup.string()
        .oneOf(map(EVENT_TYPE, t => t))
        .required(translate('form.error.mixed.required')),
      description: Yup.string(),
      tags: Yup.array()
        .min(1, translate('form.error.array.min'))
        .max(10, translate('form.error.array.max'))
        .of(
          Yup.object().shape({
            label: Yup.string().required(),
            value: Yup.string().required()
          })
        )
    }),
  mapPropsToValues: props => props.initialValues,
  handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
    setSubmitting(true);
    const {
      checkEventSlug, isEditing, translate, user
    } = props;
    const { slug, isHomepageUrl } = values;
    if (!isHomepageUrl) {
      values.externalSignupUrl = '';
    }

    if (slug && !isEditing) {
      const res = await checkEventSlug({ variables: { slug } });
      const taken = get(res, 'data.checkEventSlug.status');
      if (taken) {
        setErrors({ slug: translate('form.error.async.slugTaken') });
        setSubmitting(false);
        return;
      }
    }

    const submittedValues = {
      ...values,
      tags: values.tags.map(t => t.value),
      contentJson: JSON.stringify(values.contentJson),
      location: JSON.stringify(values.location)
    };

    props.handleSubmit(submittedValues, () => {
      if (props.templateName) {
        axios.post(PAGE_BUILDER_CREATE, {
          token: user.token,
          eventSlug: slug,
          pageBuilderUrl: `https://hh-pagebuilder.hackhub.com/?product=hackhub&slug=${slug}&pageName=homepage`,
          pageName: 'homepage',
          templateName: props.templateName
        });
      }
      setSubmitting(false);
    });
  },
  displayName: FORM_NAME.LOGIN_FORM
});

class EventForm extends Component {
  state = {
    cachedOptions: [],
    step: 1
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.isSubmitting &&
      !this.props.isSubmitting &&
      !this.props.isValid
    ) {
      const firstErrorFieldName = Object.keys(this.props.errors)[0];
      if (firstErrorFieldName) {
        scroller.scrollTo(`position-${firstErrorFieldName}`, {
          offset: -70,
          smooth: true,
          duration: 1000,
          containerId: 'ContainerElement'
        });
      }
    }
  }

  getLocationOptions = (input, callback) => {
    if (input.length < 4) {
      callback(null, { options: this.state.cachedOptions });
      return;
    }

    axios
      .post(CMS_EVENT_GET_LOCATIONS, { q: input })
      .then(({ data }) => {
        const options = map(data.locations.resourceSets[0].resources, loc => ({
          label: loc.address.formattedAddress,
          value: { address: loc.address, coordinates: loc.point.coordinates }
        }));
        this.setState({ cachedOptions: options });
        callback(null, { options });
      })
      .catch(() => {
        callback(null, { options: this.state.cachedOptions });
      });
  };

  nextStep = async (step) => {
    const { values } = this.props;
    let fieldCheckPass = false;
    let startBeforeEnd = false;
    let subBeforeEnd = false;
    let startBeforeSub = false;
    const { slug } = values;
    const res = await this.props.checkEventSlug({ variables: { slug } });
    const taken = get(res, 'data.checkEventSlug.status');
    if (step === 1) {
      fieldCheckPass = values.name !== '' && values.slug !== '' && !taken;
    } else if (step === 2) {
      if (!values.rounds || values.rounds < 2) {
        fieldCheckPass = moment(values.start).isBefore(values.end) && moment(values.start).isBefore(values.subDeadline)
          && moment(values.subDeadline).isBefore(values.end) && values.tags.length > 0;
      } else if (values.rounds && values.rounds > 1) {
        values.subDeadline = values.round1Deadline;
        fieldCheckPass = multiRoundDeadlineValidateCreate(values);
      }
      startBeforeEnd = moment(values.start).isBefore(values.end);
      subBeforeEnd = moment(values.subDeadline).isBefore(values.end);
      startBeforeSub = moment(values.start).isBefore(values.subDeadline);
    } else {
      fieldCheckPass = true;
    }

    if (fieldCheckPass) {
      this.setState({
        step: step + 1
      });
    } else if (values.name === '' || values.slug === '' || !values.tags.length) {
      HHAlert({ id: 'alert.warning.requiredField' }, ALERT_TYPE.ERROR);
    } else if (taken) {
      HHAlert({ id: 'alert.warning.slugTaken' }, ALERT_TYPE.ERROR);
    } else if (!startBeforeEnd) {
      HHAlert({ id: 'alert.warning.startBeforeEnd' }, ALERT_TYPE.ERROR);
    } else if (!startBeforeSub) {
      HHAlert({ id: 'alert.warning.startBeforeSub' }, ALERT_TYPE.ERROR);
    } else if (!subBeforeEnd) {
      HHAlert({ id: 'alert.warning.subBeforeEnd' }, ALERT_TYPE.ERROR);
    }
  }


  // Go back to prev step
  prevStep = () => {
    const { step } = this.state;
    this.setState({
      step: step - 1
    });
  };

  render() {
    const {
      values,
      handleSubmit,
      handleChangeTemplate,
      templateName,
      isEditing,
      translate,
      handleChange,
      activeLanguage,
      isSubmitting,
      user
    } = this.props;

    const { step } = this.state;
    const groupPermissions = get(user, 'groupPermissions');

    const limtOptions = map(times(10, x => x + 1), y => ({
      value: y,
      label: y
    }));

    const tagOptions = [
      { value: '5G', label: '5G' },
      { value: 'Design', label: 'Design' },
      { value: 'IoT', label: 'IoT' },
      { value: 'Education', label: 'Education' },
      { value: 'Tool', label: 'Tool' },
      { value: 'Blockchain', label: 'Blockchain' },
      { value: 'STEM', label: 'STEM' },
      { value: 'Medical', label: 'Medical' },
      { value: 'AI', label: 'AI' }
    ];

    const currentTags = values.tags.filter(tag => !tagOptions.includes(tag));
    tagOptions.concat(currentTags);


    // const currentCoverImageUrl = values.coverImageUrl
    //   ? values.coverImageUrl
    //   : BANNER_TEMPLATE.coverTrigger;
    const currentCoverImageUrl = values.coverImageTempUrl
      ? values.coverImageTempUrl
      : BANNER_TEMPLATE.coverTrigger;
    // console.log('currentCoverImageUr: ', currentCoverImageUrl);

    // const currentBannerBg = values.bannerBg
    //   ? values.bannerBg
    //   : BANNER_TEMPLATE.bannerTrigger;

    const currentBannerBg = values.bannerBgTempUrl
      ? values.bannerBgTempUrl
      : BANNER_TEMPLATE.bannerTrigger;

    const sponsorUploadTrigger = BANNER_TEMPLATE.bannerTrigger;

    let createEventPermission = false;

    if (typeof groupPermissions !== 'undefined') {
      createEventPermission = groupPermissions.includes('event.add_event');
    }

    if (isEditing) {
      return (
        <Fragment>
          <Form
            className='cms-nav'
            id='ContainerElement'
            onSubmit={handleSubmit}
          >
            <Link
              to={isEditing ? `/admin/event/${values.slug}` : '/admin'}
              className='form-cancel'
            >
              <Icon name='arrow left' />
              {translate('back')}
            </Link>

            <section className='section'>
              <Header size='large' as='h2'>
                {translate('event.information')}
              </Header>
              <Form.Field name='position-name'>
                <Element>
                  <label htmlFor='form-eventName'>
                    {translate('event.name')}
                  </label>
                  <Field
                    name='name'
                    component={HHInput}
                    otherProps={{
                      type: 'text',
                      id: 'form-eventName',
                      className: 'ui input',
                      maxLength: 64,
                      placeholder: translate('your event name')
                    }}
                  />
                </Element>
              </Form.Field>
            </section>

            <section className='section'>
              <Header size='large' as='h2'>
                {translate('banner.image')}
              </Header>
              <Form.Field className='bannerImage'>
                <Field
                  name='bannerBg'
                  component={HHFileUploader}
                  otherProps={{
                    id: 'form-bannerBg',
                    type: 'image_37x9',
                    fileType: 'event_banner',
                    slug: values.slug,
                    trigger: (
                      <Image
                        style={{ maxWidth: '100%' }}
                        src={currentBannerBg}
                        alt='uploaded file'
                      />
                    )
                  }}
                />
                <span className='aside-text'>GIF, JPG, PNG / Ratio 37:9</span>
              </Form.Field>
            </section>

            <section className='section'>
              <Header size='large' as='h2'>
                {translate('cover.image')}
              </Header>
              <Form.Field className='coverImage'>
                <Field
                  name='coverImageUrl'
                  component={HHFileUploader}
                  otherProps={{
                    id: 'form-coverImageUrl',
                    type: 'image_16x9',
                    fileType: 'event_cover',
                    slug: values.slug,
                    trigger: (
                      <Image
                        style={{ maxWidth: '100%' }}
                        src={currentCoverImageUrl}
                        alt='uploaded file'
                      />
                    )
                  }}
                />
                <span className='aside-text'>GIF, JPG, PNG / Ratio 16:9</span>
              </Form.Field>
            </section>

            {!values.isOnline ? (
              <section className='section location'>
                <Header size='large' as='h2'>
                  {translate('common.location')}
                </Header>
                <Field
                  name='location'
                  component={HHLocationSelect}
                  otherProps={{
                    async: true,
                    loadOptions: debounce(this.getLocationOptions, 1000),
                    placeholder: translate('form.placeholder.eventLocation'),
                    // disabled: !isEventCreator,
                    // Do no filtering, just return all options
                    filterOptions: options => options
                  }}
                />
              </section>
            ) : ''}

            <section className='section dateAndTime'>
              <Header size='large' as='h2'>
                {translate('date.and.time')}
              </Header>
              <Form.Field name='position-timezone'>
                <label htmlFor='form-timezone' className='team-label'>
                  {translate('timezone')}
                </label>
                <Field
                  name='timezone'
                  component={HHSingleSelect}
                  otherProps={{
                    id: 'form-timezone',
                    simpleValue: true,
                    searchable: true,
                    clearable: false,
                    options: getTimeZoneOptions(),
                    placeholder: translate('timezone')
                  }}
                />
              </Form.Field>

              <Form.Field name='position-start'>
                <label htmlFor='form-start'>
                  {translate('start.date.and.time')}
                </label>
                <i className='fal fa-calendar' />
                <Field
                  name='start'
                  component={HHDatePicker}
                  otherProps={{
                    id: 'form-start',
                    locale: activeLanguage.code,
                    placeholderText: translate('select start date')
                  }}
                />
              </Form.Field>

              <Form.Field name='position-subDeadline'>
                <label htmlFor='form-subDeadline'>
                  {translate('Submission Deadline')}
                </label>
                <i className='fal fa-calendar' />
                <Field
                  name='subDeadline'
                  component={HHDatePicker}
                  otherProps={{
                    id: 'form-subDeadline',
                    locale: activeLanguage.code,
                    minDate: moment(values.start),
                    placeholderText: translate('selectEventSubDeadline')
                  }}
                />
              </Form.Field>

              <Form.Field name='position-end'>
                <label htmlFor='form-end'>
                  {translate('end.date.and.time')}
                </label>
                <i className='fal fa-calendar' />
                <Field
                  name='end'
                  component={HHDatePicker}
                  otherProps={{
                    id: 'form-end',
                    locale: activeLanguage.code,
                    minDate: moment(values.start),
                    placeholderText: translate('select end date')
                  }}
                />
              </Form.Field>
            </section>

            <section className='section'>
              <Header size='large' as='h2'>
                {translate('team')}
              </Header>
              <Form.Field name='position-maxTeamMembers'>
                <label htmlFor='form-maxTeamMembers' className='team-label'>
                  {translate('max.team.size')}
                </label>
                <Field
                  name='maxTeamMembers'
                  component={HHSingleSelect}
                  otherProps={{
                    id: 'form-maxTeamMembers',
                    simpleValue: true,
                    searchable: false,
                    clearable: false,
                    options: limtOptions,
                    placeholder: translate('max.team.size')
                  }}
                />
              </Form.Field>
            </section>

            <section className='section'>
              <Header size='large' as='h2'>
                {translate('Event Type')}
              </Header>
              <Form.Field>
                <Field
                  name='type'
                  component={HHRadioGroup}
                  otherProps={{
                    items: map(
                      [EVENT_TYPE.FREE, EVENT_TYPE.TICKET], // EVENT_TYPE,
                      type => ({
                        label: translate(`common.${toLower(type)}`),
                        value: type
                      })
                    ),
                    radioGroupName: 'event-type',
                    disabled: isEditing
                  }}
                />
              </Form.Field>
            </section>

            <section className='section cms-nav_public'>
              <Form.Field name='position-isPrivate'>
                <Header size='large' as='h2' className='cms-nav_public_heading'>
                  {translate('public')}
                </Header>
                <Field
                  name='isPrivate'
                  component={HHCheckbox}
                  otherProps={{
                    isReversed: true,
                    toggle: true,
                    disabled: !createEventPermission
                  }}
                />
                <p className='aside-text'>{translate('going.public')}</p>
              </Form.Field>
            </section>

            <section className='section cms-nav_public'>
              <Form.Field name='position-isScreening'>
                <Header size='large' as='h2' className='cms-nav_public_heading'>
                  {translate('screening')}
                </Header>
                <Field
                  name='isScreening'
                  component={HHCheckbox}
                  otherProps={{
                    isReversed: false,
                    toggle: true
                  }}
                />
                <p className='aside-text'>{translate('screening.info')}</p>
              </Form.Field>
            </section>

            <section className='section cms-nav_public'>
              <Form.Field name='position-containStreaming'>
                <Header size='large' as='h2' className='cms-nav_public_heading'>
                  {translate('common.liveStreaming')}
                </Header>
                <Field
                  name='containStreaming'
                  component={HHCheckbox}
                  otherProps={{
                    isReversed: false,
                    toggle: true
                  }}
                />
                <p className='aside-text'>{translate('openEventChannel')}</p>
              </Form.Field>
            </section>

            <section className='section'>
              <Form.Field name='position-tags'>
                <Header size='large' as='h2' className='cms-nav_public_heading'>
                  {translate('common.topics')}
                </Header>
                <Field
                  name='tags'
                  component={HHMultipleSelect}
                  otherProps={{
                    id: 'tags',
                    options: tagOptions,
                    creatable: true,
                    placeholder: translate('chooseTheme')
                  }}
                />
              </Form.Field>
            </section>

            <section
              className={
                values.isHomepageUrl
                  ? 'section cms-nav_public'
                  : 'section cms-nav_screening'
              }
            >
              <Form.Field name='position-isHomepageUrl'>
                <Header size='large' as='h2' className='cms-nav_public_heading'>
                  {translate('externalSignupUrl')}
                </Header>
                <Field
                  name='isHomepageUrl'
                  component={HHCheckbox}
                  otherProps={{
                    isReversed: false,
                    toggle: true
                  }}
                />
                <p className='aside-text'>
                  {translate('going.externalSignupUrl')}
                </p>
              </Form.Field>
            </section>
            {values.isHomepageUrl && (
              <section className='section cms-nav_screening'>
                <Form.Field name='position-externalSignupUrl'>
                  <Header
                    size='large'
                    as='h2'
                    className='cms-nav_public_heading'
                  >
                    {translate('externalSignupUrl')}
                  </Header>
                  <Field
                    name='externalSignupUrl'
                    component={HHInput}
                    otherProps={{
                      id: 'form-externalSignupUrl',
                      placeholder: translate('http'),
                      className: 'ui input'
                    }}
                  />
                  <p className='aside-text'>
                    {translate('externalSignupUrl.required')}
                  </p>
                </Form.Field>
              </section>
            )}

            <div className='cms-event_doneBtn'>
              <Button
                loading={isSubmitting}
                // unlock manager edit event
                // disabled={isSubmitting || (isEventManager && type === 'edit')}
                disabled={isSubmitting}
              >
                <Icon name='check' />
                {isEditing
                  ? translate('Done Editing')
                  : translate('Create Event')}
              </Button>
            </div>
          </Form>

          <EventFormDisplay {...this.props} />
        </Fragment>
      );
    }
    switch (step) {
      case 1:
        return (
          <EventFormBasicInfo
            nextStep={this.nextStep}
            values={values}
            isEditing={isEditing}
            handleChange={handleChange}
            getLocationOptions={this.getLocationOptions}
          />
        );
      case 2:
        return (
          <EventFormDetailInfo
            prevStep={this.prevStep}
            nextStep={this.nextStep}
            values={values}
            isEditing={isEditing}
            handleChange={handleChange}
            activeLanguage={activeLanguage}
            createEventPermission={createEventPermission}
            getLocationOptions={this.getLocationOptions}
            sponsorUploadTrigger={sponsorUploadTrigger}
            user={user}
          />
        );
      case 3:
        return (
          <EventFormImage
            prevStep={this.prevStep}
            nextStep={this.nextStep}
            values={values}
            isEditing={isEditing}
            templateName={templateName}
            handleChangeTemplate={handleChangeTemplate}
            currentBannerBg={currentBannerBg}
            currentCoverImageUrl={currentCoverImageUrl}
            user={user}
          />
        );
      case 4:
        return (
          <EventFormPreview
            prevStep={this.prevStep}
            nextStep={this.nextStep}
            values={values}
            isEditing={isEditing}
            isSubmitting={isSubmitting}
            handleChange={handleChange}
            activeLanguage={activeLanguage}
            handleSubmit={handleSubmit}
          />
        );
      default:
        return <div>create event default/error</div>;
    }
  }
}
export default compose(
  withLocalize,
  connect(({ user, permissions }) => ({ user, permissions })),
  withPermissionValidation(({ permissions }) => ({
    permissions: EVENT_CREATOR,
    grantPermissions: permissions,
    propName: 'isEventCreator'
  })),
  withPermissionValidation(({ permissions }) => ({
    permissions: EVENT_MANAGER,
    grantPermissions: permissions,
    propName: 'isEventManager'
  })),
  graphql(CHECK_EVENT_SLUG, { name: 'checkEventSlug' }),
  formikEnhancer
)(EventForm);



// WEBPACK FOOTER //
// ./src/components/forms/eventForm.js